import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type { CatalogEntry, CatalogField } from '@safetyhub/shared';
import { adminApi } from '../../lib/api';
import type { BindingSuggestField, BindingSuggestion } from '../../lib/api';
import { formatElapsed, useElapsedSeconds } from '../../hooks/useElapsedSeconds';
import {
  bindingSuggestCacheKey,
  mergeBindingSuggestions,
  parseBindingSuggestCache,
} from './formImportOps';
import type { AppliedBindingSummary } from './formImportOps';

/**
 * 데이터 연결 후보 제안 모달 (form-import P2) — 현재 편집 상태의 수동 입력 필드 목록을
 * AI에게 보내 카탈로그 매칭 후보를 받고, 항목별/전체 적용으로 body 바인딩 prefillFrom을
 * 설정한다(적용은 부모의 undo 퍼널 — 저장은 사용자가 기존 저장 버튼으로).
 *
 * 설계 정본: docs/decisions/2026-07-27-form-import-p2-binding-suggest.md
 * 임포트 초안 전용이 아니라 손저작 템플릿에도 쓰는 범용 도구다.
 */

interface BindingSuggestModalProps {
  open: boolean;
  /**
   * 캐시 키(현재 템플릿 키) — 있으면 직전 분석 결과를 localStorage에서 재사용하고,
   * 새 AI 호출은 "재분석" 버튼으로만 일어난다. 미저장 새 문서(null)면 캐시 없이 매번 분석.
   */
  templateKey?: string | null;
  /** 요청 대상 필드(부모가 collectBindingSuggestFields로 추출 — 이미 연결된 필드 제외됨). */
  fields: BindingSuggestField[];
  /** 후보 표시명(entry.label + 반환 필드 label) 해소용 카탈로그. */
  catalog: CatalogEntry[];
  /**
   * 후보 적용 — 부모가 apply(undo 1스텝)로 body 바인딩을 갱신한다.
   *  - 'connect'(기본): 카탈로그 완전 연결 — 시스템 값 자동 유입, 작성 입력칸에서 제외
   *    ("최소 수동입력" 제품 원칙 — 2026-07-27 사용자 확정으로 이쪽이 최우선).
   *  - 'prefill': 자동 채움 — 입력칸 유지 + 시스템 값을 초기값으로(작성자 확인·수정 여지).
   */
  onApply: (items: BindingSuggestion[], mode: 'connect' | 'prefill') => void;
  /**
   * 적용된 연결/채움 현황(부모가 collectAppliedBindings로 라이브 파생 — 해제 즉시 반영).
   * 이 모달로 적용한 것과 손 연결을 구분 저장하지 않으므로 둘 다 보인다(자동 채움 현황판).
   */
  applied: AppliedBindingSummary[];
  /** 항목 해제 — 부모가 disconnectAppliedBindings를 apply(undo 1스텝)로 실행한다. */
  onDisconnect: (bindingKey: string) => void;
  onClose: () => void;
}

type FetchState =
  | { status: 'loading' }
  | { status: 'done'; suggestions: BindingSuggestion[]; fetchedAt: number; fromCache: boolean }
  | { status: 'error'; message: string };

/** select 경로의 카탈로그 반환 필드 라벨 해소(dot 중첩 하강) — 못 찾으면 경로 그대로. */
function selectFieldLabel(entry: CatalogEntry | undefined, select: string): string {
  if (!entry) return select;
  let fields: CatalogField[] | undefined = entry.returns;
  let label: string | undefined;
  for (const seg of select.split('.')) {
    const matched: CatalogField | undefined = fields?.find((f) => f.path === seg);
    if (!matched) return select;
    label = matched.label;
    fields = matched.fields;
  }
  return label ?? select;
}

export function BindingSuggestModal({
  open,
  templateKey,
  fields,
  catalog,
  onApply,
  applied,
  onDisconnect,
  onClose,
}: BindingSuggestModalProps) {
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' });
  // 적용(잠금) 판정은 세션 로컬 상태가 아니라 **적용 현황(applied prop, 템플릿 라이브 파생)**에서
  // 파생한다 — 적용하면 잠기고 해제하면 자동으로 풀리며, 적용 실패 시 잘못 잠기지 않는다.
  const appliedSet = useMemo(() => new Set(applied.map((a) => a.bindingKey)), [applied]);
  // AI 응답 대기의 "살아 있음" 신호 — 서버가 진행률을 주지 않는 단발 호출이라 가짜 바를
  // 움직이는 대신 경과 시간을 센다. 닫힌 동안·완료 후에는 멈추고, 재분석마다 0에서 다시 센다.
  const elapsedSeconds = useElapsedSeconds(open && fetchState.status === 'loading');

  /**
   * 분석 실행 — force=false면 캐시 우선(있으면 AI 호출 없이 표시), force=true(재분석)면 새 호출.
   * 성공 결과는 템플릿 키별 localStorage에 저장해 다음 열림에서 재사용한다.
   * cancelled 처리를 위해 취소 플래그 참조를 받는다(열림 effect의 cleanup과 연동).
   */
  const analyze = useCallback(
    (force: boolean, isCancelled: () => boolean) => {
      // 캐시 확인을 빈 필드 체크보다 먼저 — 모든 필드가 이미 연결된 상태로 열어도 캐시를
      // 적재해 둬야, 해제로 필드가 복귀했을 때 직전 후보가 바로 살아난다(전체 보관 모델).
      if (!force && templateKey) {
        let cached = null;
        try {
          cached = parseBindingSuggestCache(localStorage.getItem(bindingSuggestCacheKey(templateKey)));
        } catch {
          cached = null;
        }
        if (cached) {
          setFetchState({
            status: 'done',
            suggestions: cached.suggestions,
            fetchedAt: cached.fetchedAt,
            fromCache: true,
          });
          return;
        }
      }
      if (fields.length === 0) {
        // 요청할 필드가 없고 캐시도 없으면 호출 없이 빈 결과로 안내.
        setFetchState({ status: 'done', suggestions: [], fetchedAt: Date.now(), fromCache: false });
        return;
      }
      setFetchState({ status: 'loading' });
      adminApi
        .suggestBindings(fields)
        .then((res) => {
          if (isCancelled()) return;
          if (res.success && res.data) {
            const fetchedAt = Date.now();
            // 직전 캐시와 병합 — 이번 요청에 없던(이미 연결된) 필드의 후보를 보존해, 해제
            // 복귀 시 직전 제안이 살아 있게 한다(요청에 있던 키는 새 결과가 정본).
            let merged = res.data.suggestions;
            if (templateKey) {
              try {
                const prev = parseBindingSuggestCache(
                  localStorage.getItem(bindingSuggestCacheKey(templateKey)),
                );
                merged = mergeBindingSuggestions(
                  res.data.suggestions,
                  prev,
                  fields.map((f) => f.bindingKey),
                );
                localStorage.setItem(
                  bindingSuggestCacheKey(templateKey),
                  JSON.stringify({ suggestions: merged, fetchedAt }),
                );
              } catch {
                // 저장 실패(용량 등)는 무해 — 다음 열림에 새로 분석할 뿐.
              }
            }
            setFetchState({ status: 'done', suggestions: merged, fetchedAt, fromCache: false });
          } else {
            setFetchState({
              status: 'error',
              message: res.error?.message ?? '후보 제안에 실패했습니다.',
            });
          }
        })
        .catch((err: unknown) => {
          if (isCancelled()) return;
          // 활성 provider 미설정/AI 실패는 서버가 503 + 메시지로 알린다 — 그대로 표시.
          const message =
            (err as { response?: { data?: { error?: { message?: string } } } }).response?.data
              ?.error?.message ?? (err as Error).message;
          setFetchState({ status: 'error', message: message || '후보 제안에 실패했습니다.' });
        });
    },
    [fields, templateKey],
  );

  // 열릴 때 1회 — 캐시가 있으면 호출 없이 직전 후보를 보여 준다(새 호출은 "재분석"으로만).
  // deps는 open만: 해제 복귀로 fields prop이 커져도 재분석/캐시 재읽기를 트리거하지 않는다
  // (후보 상태가 전체 보관이라 표시 필터만으로 충분 — 미저장 템플릿의 AI 재호출 방지).
  // analyze 최신 참조는 ref로 유지(재분석 버튼은 자기 closure의 최신 analyze를 직접 호출).
  const analyzeRef = useRef(analyze);
  analyzeRef.current = analyze;
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    analyzeRef.current(false, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const suggestions = fetchState.status === 'done' ? fetchState.suggestions : [];
  // 필드 순서 보존 그룹핑 — 필드별 후보 목록(제안 없는 필드도 행으로 노출해 결과를 설명).
  // 후보 상태는 전체 보관이므로 현재 필드와의 교집합은 여기(표시)에서 걸러진다.
  const rows = useMemo(
    () =>
      fields.map((f) => ({
        field: f,
        items: suggestions.filter((s) => s.bindingKey === f.bindingKey),
      })),
    [fields, suggestions],
  );
  const remaining = useMemo(() => {
    const fieldSet = new Set(fields.map((f) => f.bindingKey));
    return suggestions.filter((s) => fieldSet.has(s.bindingKey) && !appliedSet.has(s.bindingKey));
  }, [suggestions, fields, appliedSet]);

  const applyItems = (items: BindingSuggestion[], mode: 'connect' | 'prefill') => {
    if (items.length === 0) return;
    onApply(items, mode);
    // 행 잠금은 별도 기록 없음 — 부모 템플릿 갱신 → applied prop 재파생으로 자동 반영.
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="데이터 연결 후보 제안"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
          {/* 브랜드 테두리 유지 — 재분석은 AI를 새로 호출(비용·대기)하는 액션이라 옆의 `닫기`와
              같은 모양이면 오클릭 대가가 크다. `Button`에 outline-brand variant가 없어 색만 얹는다. */}
          <Button
            variant="secondary"
            className="border-brand-300 text-brand-600 hover:bg-brand-50 hover:text-brand-700"
            // 캐시/직전 결과가 마음에 안 들 때 명시적으로만 새 AI 호출(비용·대기 절감의 짝).
            onClick={() => analyze(true, () => false)}
            disabled={fetchState.status === 'loading' || fields.length === 0}
          >
            재분석
          </Button>
          <Button
            variant="primary"
            onClick={() => applyItems(remaining, 'connect')}
            disabled={remaining.length === 0}
          >
            전체 연결 ({remaining.length})
          </Button>
        </>
      }
    >
      {fetchState.status === 'loading' ? (
        <div className="space-y-2 py-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm text-gray-600">
              AI가 입력 필드 {fields.length}개와 시스템 데이터(시스템 조회)의 연결 후보를 찾고
              있습니다…
            </p>
            <span className="shrink-0 text-sm tabular-nums text-gray-500">
              {formatElapsed(elapsedSeconds)} 경과
            </span>
          </div>
          {/* 진행률을 알 수 없는 단발 AI 호출 — 불확정 바(pulse는 "정지"로도 읽혀 생존 신호가 약하다) */}
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-label="AI 분석 진행 중"
          >
            <div className="h-full w-1/3 rounded-full bg-brand-600 motion-safe:animate-indeterminate-bar" />
          </div>
          <p className="text-xs leading-relaxed text-gray-500">
            필드가 많거나 상위 모델을 쓰면 <b className="font-medium">최대 5분</b>까지 걸릴 수
            있습니다.
          </p>
        </div>
      ) : null}

      {fetchState.status === 'error' ? (
        <p className="rounded bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700">
          {fetchState.message}
        </p>
      ) : null}

      {fetchState.status === 'done' ? (
        fields.length === 0 ? (
          <p className="text-sm leading-relaxed text-gray-600">
            제안할 입력 필드가 없습니다 — 모든 필드가 이미 자동 채움에 연결되어 있거나, 수동
            입력 필드가 없는 템플릿입니다.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs leading-relaxed text-gray-500">
              <b>연결</b>은 그 칸을 시스템 데이터에 직접 연결합니다 — 값이 자동으로 들어오고
              작성 화면의 입력칸에서 빠집니다(수동입력 최소화, 권장). <b>채움</b>은 입력칸을
              유지한 채 시스템 값을 초기값으로만 넣습니다(작성자가 확인·수정할 값에). 적용 후{' '}
              <b>저장</b>해야 반영됩니다(실행취소 가능).
            </p>
            {fetchState.status === 'done' && fetchState.fromCache ? (
              <p className="rounded border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs leading-relaxed text-brand-700">
                {new Date(fetchState.fetchedAt).toLocaleString('ko-KR', {
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                에 분석한 결과입니다 — 후보가 마음에 들지 않으면 <b>재분석</b>을 누르세요.
              </p>
            ) : null}
            <ul className="max-h-96 space-y-2 overflow-y-auto pr-1">
              {rows.map(({ field, items }) => {
                const isApplied = appliedSet.has(field.bindingKey);
                return (
                  <li
                    key={field.bindingKey}
                    className={`rounded border px-3 py-2 ${
                      isApplied ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        {field.label ?? field.bindingKey}
                      </span>
                      <span className="truncate font-mono text-[10px] text-gray-400">
                        {field.bindingKey}
                      </span>
                      {isApplied ? (
                        <span className="ml-auto shrink-0 text-[11px] font-medium text-green-600">
                          적용됨
                        </span>
                      ) : null}
                    </div>
                    {items.length === 0 ? (
                      <p className="mt-1 text-xs text-gray-400">
                        어울리는 시스템 데이터를 찾지 못했습니다 — <b>재분석</b>을 누르면 이
                        필드를 포함해 다시 매칭합니다.
                      </p>
                    ) : (
                      <ul className="mt-1.5 space-y-1.5">
                        {items.map((s, i) => {
                          const entry = catalog.find((e) => e.key === s.catalogKey);
                          return (
                            <li key={`${s.catalogKey}-${s.select}-${i}`} className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-700">
                                  <span className="font-medium">{entry?.label ?? s.catalogKey}</span>
                                  {' · '}
                                  {selectFieldLabel(entry, s.select)}
                                  <span className="ml-1.5 text-[10px] tabular-nums text-gray-400">
                                    확신도 {Math.round(s.confidence * 100)}%
                                  </span>
                                </p>
                                <p className="text-[11px] leading-snug text-gray-400">{s.reason}</p>
                              </div>
                              <div className="flex shrink-0 gap-1">
                                <Button
                                  variant="primary"
                                  size="xs"
                                  onClick={() => applyItems([s], 'connect')}
                                  disabled={isApplied}
                                  title="시스템 데이터에 직접 연결 — 값 자동 유입, 작성 입력칸에서 제외 (권장)"
                                >
                                  연결
                                </Button>
                                {/* 채움은 연결의 약한 짝 — 같은 브랜드 계열의 테두리형으로 서열을 표시한다. */}
                                <Button
                                  variant="secondary"
                                  size="xs"
                                  className="border-brand-300 text-brand-600 hover:bg-brand-50 hover:text-brand-700"
                                  onClick={() => applyItems([s], 'prefill')}
                                  disabled={isApplied}
                                  title="입력칸 유지 + 시스템 값을 초기값으로만 (작성자가 확인·수정할 값에)"
                                >
                                  채움
                                </Button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )
      ) : null}

      {/* 적용된 연결 현황 — 템플릿 파생(라이브)이라 fetch 상태와 무관하게 표시. 해제는 행별
          명시 버튼 + 부모 undo 퍼널(실행취소 가능). 해제하면 부모가 필드를 후보 목록에 즉시
          추가하고, 전체 보관된 후보에서 직전 제안이 바로 되살아난다. */}
      {applied.length > 0 ? (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-1.5 text-xs font-medium text-gray-600">적용된 연결 ({applied.length})</p>
          <ul className="max-h-48 space-y-1 overflow-y-auto pr-1">
            {applied.map((a) => {
              const entry = catalog.find((e) => e.key === a.catalogKey);
              // 연도 슬롯 등 행 인덱스 접두(`1.workerCount`)는 라벨 해소 시 벗긴다(표시는 필드 라벨).
              const sel = a.select?.replace(/^\d+\./, '');
              return (
                <li
                  key={a.bindingKey}
                  className="flex items-center gap-2 rounded border border-gray-100 bg-gray-50/60 px-2.5 py-1.5"
                >
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      a.mode === 'connect'
                        ? 'bg-brand-100 text-brand-700'
                        : 'border border-brand-200 text-brand-600'
                    }`}
                  >
                    {a.mode === 'connect' ? '연결' : '채움'}
                  </span>
                  <span className="truncate text-xs text-gray-700">{a.label ?? a.bindingKey}</span>
                  <span className="truncate text-[11px] text-gray-400">
                    → {entry?.label ?? a.catalogKey}
                    {sel ? ` · ${selectFieldLabel(entry, sel)}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => onDisconnect(a.bindingKey)}
                    title="자동 채움 출처를 제거하고 수동 입력으로 되돌립니다 (저장 전 실행취소 가능)"
                    className="ml-auto shrink-0 rounded border border-gray-300 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-white"
                  >
                    해제
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}
